import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks/expenditure-budget-purpose';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import { useSnapshots } from '@/features/tenant-manager/snapshots/_hooks/useSnapshot';
import { findUpperRecursive } from '@/utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type z from 'zod';
import type { ProjectModel } from '../../_models/project';

export const useUpsertFormInitialValue = (
  project: z.infer<typeof ProjectModel> | undefined,
) => {
  const snapshots = useSnapshots({
    params: { referenceFiscalYear: project?.fiscalYear?.toString() },
    options: { onlySuccess: true },
    enabled: !!project?.fiscalYear,
  });

  const initialSnapshot = useMemo<string[]>(() => {
    const snapshot = snapshots.data?.find((v) => v.id === project?.snapshotId);
    const year = dayjs(snapshot?.referenceDate).year().toString();
    if (!year || !project?.snapshotId) {
      return [];
    }
    return [year, project?.snapshotId];
  }, [snapshots.data, project?.snapshotId]);

  const organizationIds = useMemo(
    () =>
      [
        ...(project?.organization.upperIds ?? []),
        project?.organization.id,
      ].flatMap((v) => v ?? []),
    [project?.organization.upperIds, project?.organization.id],
  );

  const nestedRelatedOrganizationIds = useMemo(() => {
    const relatedOrganizations = project?.relatedOrganizations ?? [];
    return relatedOrganizations.map((v) =>
      [...v.upperIds, v.id].flatMap((v) => v ?? []),
    );
  }, [project?.relatedOrganizations]);

  const nestedOtherResponsibleOrganizationIds = useMemo(() => {
    const otherResponsibleOrganizations =
      project?.otherResponsibleOrganizations ?? [];
    return otherResponsibleOrganizations.map((v) =>
      [...v.upperIds, v.id].flatMap((v) => v ?? []),
    );
  }, [project?.otherResponsibleOrganizations]);

  const independentAdministrativeAgencyIds = useMemo(
    () =>
      [
        ...(project?.independentAdministrativeAgency?.upperIds ?? []),
        project?.independentAdministrativeAgency?.id,
      ].flatMap((v) => v ?? []),
    [project?.independentAdministrativeAgency],
  );

  const fundAdditionalAttributes = useMemo(() => {
    const fundAdditionalAttributes = project?.fundAdditionalAttributes;

    const originalAdditionalJurisdictionalUsers =
      fundAdditionalAttributes?.additionalJurisdictionalUsers;

    if (!project || project.sheetType !== 'KS' || !fundAdditionalAttributes) {
      return fundAdditionalAttributes
        ? { ...fundAdditionalAttributes, additionalJurisdictionalUsers: [] }
        : null;
    }

    const { additionalJurisdictionalOrganizations } = project;

    const additionalJurisdictionalUsers =
      originalAdditionalJurisdictionalUsers?.map<{
        ministryId: string;
        organizationIds: string[];
        peopleInChargeIds: string[];
        businessManagerIds: string[];
      }>((v) => {
        const organization = additionalJurisdictionalOrganizations.find(
          (org) => v.organizationId === org.id,
        );
        return {
          ministryId: v.ministryId,
          organizationIds: [
            ...(organization?.upperIds ?? []),
            organization?.id,
          ].flatMap((v) => v ?? []),
          peopleInChargeIds: v.peopleInChargeIds,
          businessManagerIds: v.businessManagerIds,
        };
      });

    return {
      ...fundAdditionalAttributes,
      additionalJurisdictionalUsers,
      operationForms: fundAdditionalAttributes.operationForms ?? [],
      businessForms: fundAdditionalAttributes.businessForms ?? [],
    };
  }, [project]);

  // レビュー対象外のみ使用
  const expenditureBudgetPurpose = useExpenditureBudgetPurpose({
    id: project?.majorExpenditureBudgetPurposeId ?? '',
  });

  const { data: policiesData, isPending: isLoadingPolicies } = usePolicies({
    enabled: project?.sheetType === 'RS',
  });

  const isLoading =
    (project?.sheetType === 'RS' && isLoadingPolicies) ||
    (project?.majorExpenditureBudgetPurposeId &&
      expenditureBudgetPurpose.isPending) ||
    (project?.fiscalYear && snapshots.isPending);

  return {
    isInitialDataFetchedLoading: isLoading,
    initialSnapshot,
    policiesForProject: project?.policiesForProject.map((policyForProject) => {
      const currentPolicies = findUpperRecursive(
        policyForProject.policyId,
        policiesData ?? [],
      ).reverse();
      return {
        policyId: currentPolicies.map((d) => d.id),
        policyUrl: policyForProject.policyUrl,
      };
    }),
    organizationIds,
    nestedRelatedOrganizationIds,
    nestedOtherResponsibleOrganizationIds,
    independentAdministrativeAgencyIds,
    fundAdditionalAttributes,
    expenditureBudgetPurpose: expenditureBudgetPurpose.data,
  };
};
